import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SearchTray.module.css';
import { useSearch } from '../../hooks/useSearch';
import { useRecentSearches } from '../../hooks/useRecentSearches';
import { useBranchStore } from '../../stores/branchStore';
import { SearchResultRow } from './SearchResultRow';
import { SearchSkeleton } from './SearchSkeleton';
import { SearchEmptyState } from './SearchEmptyState';
import { RecentSearchesList } from './RecentSearchesList';
import { SuggestedCategoriesList } from './SuggestedCategoriesList';

const MIN_QUERY_LENGTH = 2;
const RESULT_LIMIT = 8;

export interface SearchTrayProps {
  /** True when the tray is visually open (parent controls open/close). */
  open: boolean;
  /** Live query — already-trimmed string from the input. */
  query: string;
  /** Debounced version of `query` (200ms upstream) used to trigger the FTS fetch. */
  debouncedQuery: string;
  /** Called when a recent-search row or suggested-category is activated; the parent should set its query state to this value. */
  onActivateRecent: (q: string) => void;
  /** Called when the user picks any result (row click / Enter on highlight). The tray expects the parent to close itself. */
  onClose: () => void;
}

type FlatItem =
  | { kind: 'content'; id: string; slug: string; title: string; breadcrumb: string | null }
  | { kind: 'category'; id: string; name: string; section: string; title: string; breadcrumb: string | null }
  | {
      kind: 'subcategory';
      id: string;
      categoryId: string;
      categoryName: string;
      section: string;
      title: string;
      breadcrumb: string | null;
    };

export function SearchTray({ open, query, debouncedQuery, onActivateRecent, onClose }: SearchTrayProps) {
  const navigate = useNavigate();
  const { data, isPending, isFetching } = useSearch(debouncedQuery, RESULT_LIMIT);
  const { items: recents, push: pushRecent, remove: removeRecent, clear: clearRecents } = useRecentSearches();
  // Search returns main-branch FTS hits. If the user is currently in a
  // branch (view or edit), navigating to /library/{slug} would otherwise
  // be a no-op — Library.tsx skips useContentBySlug while isInBranchMode
  // is true, so the page stays on the previous content. Clearing the
  // branch context drops the user back onto main so the new URL resolves.
  const setCurrentBranch = useBranchStore((s) => s.setCurrentBranch);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const rowRefs = useRef<Array<HTMLButtonElement | null>>([]);
  // Sliding highlight position — animates between active rows' offsetTop +
  // offsetHeight. Starts invisible so the first measurement doesn't slide
  // in from (0, 0).
  const [highlight, setHighlight] = useState<{ top: number; height: number; visible: boolean }>(
    { top: 0, height: 0, visible: false }
  );

  const isAboveThreshold = debouncedQuery.trim().length >= MIN_QUERY_LENGTH;
  // `isPending` is true on every refetch (incl. cached refetch). The first
  // fetch for a fresh query is signaled by `isFetching && !data` — that's
  // when we render the skeleton. Subsequent cache hits render data
  // immediately even while a background refetch is happening.
  const showSkeleton = isAboveThreshold && (isPending || (isFetching && !data));

  // Flatten the 3 groups for keyboard nav.
  const flatItems = useMemo<FlatItem[]>(() => {
    if (!data) return [];
    return [
      ...data.contents.map((c) => ({
        kind: 'content' as const,
        id: c.id,
        slug: c.slug,
        title: c.title,
        breadcrumb: c.section ?? null,
      })),
      ...data.categories.map((c) => ({
        kind: 'category' as const,
        id: c.id,
        name: c.name,
        section: c.section,
        title: c.name,
        breadcrumb: c.section ?? null,
      })),
      ...data.subcategories.map((s) => ({
        kind: 'subcategory' as const,
        id: s.id,
        categoryId: s.categoryId,
        categoryName: s.categoryName,
        section: s.section,
        title: s.name,
        breadcrumb: `${s.section} › ${s.categoryName}`,
      })),
    ];
  }, [data]);

  // Reset highlight when the result set changes.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [debouncedQuery, flatItems.length]);

  const activate = useCallback(
    (item: FlatItem) => {
      pushRecent(query);
      // Drop any active branch context — see comment on `setCurrentBranch`.
      // Without this, slug-based navigation is a no-op for users sitting
      // in a branch (the recurring search-bug case from the screen
      // recording on 2026-05-14).
      setCurrentBranch(null);
      if (item.kind === 'content') {
        navigate(`/library/${item.slug}`);
      } else if (item.kind === 'category') {
        // Library reads `?category=<name>` (matches persistentCategoryList by
        // c.name === categoryParam), not by id. Section param is the plural
        // form (`brands` not `brand`) — Library strips the trailing 's'.
        navigate(
          `/library?section=${encodeURIComponent(item.section)}s&category=${encodeURIComponent(item.name)}`
        );
      } else {
        // Subcategory landing needs section + parent category name +
        // subcategoryId so isLandingPageMode resolves to 'subcategory'
        // and the subcategory DTO can be looked up by id.
        navigate(
          `/library?section=${encodeURIComponent(item.section)}s` +
            `&category=${encodeURIComponent(item.categoryName)}` +
            `&subcategoryId=${encodeURIComponent(item.id)}`
        );
      }
      onClose();
    },
    [navigate, pushRecent, query, onClose, setCurrentBranch]
  );

  // Keyboard navigation when the tray is open.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (flatItems.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, flatItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        const item = flatItems[highlightedIndex] ?? flatItems[0];
        if (item) {
          e.preventDefault();
          activate(item);
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, flatItems, highlightedIndex, activate]);

  // Scroll the highlighted row into view.
  useEffect(() => {
    const el = rowRefs.current[highlightedIndex];
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  // Sliding highlight: measure the active row and animate the chip's
  // transform/height between positions. Layout-effect so the measurement
  // happens after the DOM has the latest row layout (groups appearing,
  // results count changing, etc.).
  useLayoutEffect(() => {
    if (flatItems.length === 0) {
      setHighlight((s) => ({ ...s, visible: false }));
      return;
    }
    const el = rowRefs.current[highlightedIndex];
    if (!el) {
      setHighlight((s) => ({ ...s, visible: false }));
      return;
    }
    setHighlight({ top: el.offsetTop, height: el.offsetHeight, visible: true });
  }, [highlightedIndex, flatItems]);

  return (
    <div className={styles.wrapper} data-open={open ? 'true' : 'false'}>
      <div className={styles.panelHost}>
        <div className={styles.panelHostInner}>
        <div className={styles.panel} role="listbox" aria-label="Search results">
          {/* Sliding highlight chip — single absolute element that animates
              between the active row's offsetTop / offsetHeight. */}
          {flatItems.length > 0 && (
            <div
              className={styles.rowHighlight}
              style={{
                transform: `translateY(${highlight.top}px)`,
                height: `${highlight.height}px`,
                opacity: highlight.visible ? 1 : 0,
              }}
              aria-hidden
            />
          )}
          {!isAboveThreshold && (
            <>
              <RecentSearchesList
                items={recents}
                onActivate={onActivateRecent}
                onRemove={removeRecent}
                onClearAll={clearRecents}
              />
              <SuggestedCategoriesList
                onSelect={({ name, section }) => {
                  setCurrentBranch(null);
                  navigate(
                    `/library?section=${encodeURIComponent(section)}s&category=${encodeURIComponent(name)}`
                  );
                  onClose();
                }}
              />
              {recents.length === 0 && (
                <div className={styles.emptyState}>
                  <p className={styles.emptyStateBody}>Start typing to search categories, sub-categories, and documents.</p>
                </div>
              )}
            </>
          )}

          {isAboveThreshold && showSkeleton && <SearchSkeleton />}

          {isAboveThreshold && !showSkeleton && flatItems.length === 0 && (
            <SearchEmptyState query={debouncedQuery} />
          )}

          {isAboveThreshold && !showSkeleton && flatItems.length > 0 && (
            <>
              {data && data.contents.length > 0 && (
                <div className={styles.group}>
                  <div className={styles.groupLabel}>Documents</div>
                  {data.contents.map((c, idx) => (
                    <SearchResultRow
                      key={c.id}
                      icon={<FileIcon />}
                      title={c.title}
                      breadcrumb={c.section}
                      query={debouncedQuery}
                      highlighted={idx === highlightedIndex}
                      innerRef={(el) => { rowRefs.current[idx] = el; }}
                      onClick={() => activate(flatItems[idx])}
                      onHover={() => setHighlightedIndex(idx)}
                    />
                  ))}
                </div>
              )}
              {data && data.categories.length > 0 && (
                <div className={styles.group}>
                  <div className={styles.groupLabel}>Categories</div>
                  {data.categories.map((c, i) => {
                    const idx = data.contents.length + i;
                    return (
                      <SearchResultRow
                        key={c.id}
                        icon={<FolderIcon />}
                        title={c.name}
                        breadcrumb={c.section}
                        query={debouncedQuery}
                        highlighted={idx === highlightedIndex}
                        innerRef={(el) => { rowRefs.current[idx] = el; }}
                        onClick={() => activate(flatItems[idx])}
                        onHover={() => setHighlightedIndex(idx)}
                      />
                    );
                  })}
                </div>
              )}
              {data && data.subcategories.length > 0 && (
                <div className={styles.group}>
                  <div className={styles.groupLabel}>Sub-categories</div>
                  {data.subcategories.map((s, i) => {
                    const idx = data.contents.length + data.categories.length + i;
                    return (
                      <SearchResultRow
                        key={s.id}
                        icon={<FolderIcon />}
                        title={s.name}
                        breadcrumb={null}
                        query={debouncedQuery}
                        highlighted={idx === highlightedIndex}
                        innerRef={(el) => { rowRefs.current[idx] = el; }}
                        onClick={() => activate(flatItems[idx])}
                        onHover={() => setHighlightedIndex(idx)}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

function FileIcon() {
  return (
    <svg viewBox="0 0 16 16" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 1.5h6L13 5v9.5H3z" />
      <path d="M9 1.5V5h4" />
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg viewBox="0 0 16 16" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M1.5 4.5h5l1.5 1.5h6.5v8.5h-13z" />
    </svg>
  );
}
